import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { INVESTOR_NAV_LABEL } from '@workspace/seedforge-ui/lib/investor-nav'
import { useState } from 'react'
import { AssistantGate } from '../../../../features/investor-assistant/components/assistant-gate'
import { PassedTab } from '../../../../features/investor-assistant/components/following/passed-tab'
import { UpdatesTab } from '../../../../features/investor-assistant/components/following/updates-tab'
import { WatchdogBox } from '../../../../features/investor-assistant/components/following/watchdog-box'
import { WatchlistTab } from '../../../../features/investor-assistant/components/following/watchlist-tab'
import { InviteStrip } from '../../../../features/investor-assistant/components/invites/invite-strip'
import { InvitesSection } from '../../../../features/investor-assistant/components/invites/invites-section'
import { StartupSidePanel } from '../../../../features/investor-assistant/components/relationship/startup-side-panel'
import {
  lastView,
  rememberView,
} from '../../../../features/investor-assistant/components/remembered-view'
import {
  useFollowsQuery,
  useNotesQuery,
  useUnreadUpdates,
} from '../../../../features/investor-assistant/hooks/use-investor-assistant'

export const Route = createFileRoute('/app/investor/$id/follows')({
  component: FollowsPage,
  // The panel is remembered, so leaving the page and coming back reopens it unless the URL names one.
  beforeLoad: ({ params, search }) => {
    if (search.open) return
    const { open } = lastView('follows', params.id)
    if (open)
      throw redirect({ to: '/app/investor/$id/follows', params, search: { open }, replace: true })
  },
  validateSearch: (search: Record<string, unknown>): { open?: string; note?: true } => ({
    ...(typeof search.open === 'string' && search.open && { open: search.open }),
    ...(search.note === true && { note: true as const }),
  }),
})

type Tab = 'watchlist' | 'updates' | 'passed'

function TabButton({
  active,
  onClick,
  children,
  count,
  news = false,
}: {
  active: boolean
  onClick: () => void
  children: string
  count: string
  /** The count is news the investor has not seen ("2 new"), shown in the accent colour. */
  news?: boolean
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-1.5 border-b-2 pb-2.5 text-sm transition-colors ${
        active
          ? 'border-foreground font-medium text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
      <span
        className={`text-xs tabular-nums ${news ? 'font-medium text-primary' : 'font-normal text-muted-foreground'}`}
      >
        {count}
      </span>
    </button>
  )
}

function FollowsPage() {
  const [tab, setTab] = useState<Tab>('watchlist')
  const { open, note } = Route.useSearch()
  const { id: investorId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  // A row's "Write a note" opens the panel with its note already in edit mode.
  const setOpen = (startupId: string | undefined, writeNote = false) => {
    rememberView('follows', investorId, { open: startupId })
    return navigate({
      search: startupId ? { open: startupId, ...(writeNote && { note: true as const }) } : {},
      replace: false,
    })
  }
  const follows = useFollowsQuery()
  const notes = useNotesQuery()

  const watching = (follows.data?.data ?? []).filter((f) => f.status !== 'ENDED_BY_INVESTOR').length
  const unread = useUnreadUpdates()
  const passed = notes.data?.data.passes.length ?? 0
  // The panel opens only for what this page lists: a startup still followed, or passed. A stopped
  // follow is not on the page, so its address shows nothing.
  const listed = new Set([
    ...(follows.data?.data ?? [])
      .filter((f) => f.status !== 'ENDED_BY_INVESTOR')
      .map((f) => f.startupId),
    ...(notes.data?.data.passes ?? []).map((pass) => pass.startupId),
  ])

  return (
    <AssistantGate
      activeNav={INVESTOR_NAV_LABEL.following}
      sidePanel={
        open && listed.has(open) ? (
          <StartupSidePanel
            key={open}
            startupId={open}
            writeNote={note}
            onClose={() => setOpen(undefined)}
          />
        ) : undefined
      }
    >
      <div className="py-2 pb-10">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Following</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your watchlist, and everything the founders on it share.
          </p>
        </div>

        <div className="mt-6">
          <InviteStrip />
        </div>

        <div role="tablist" className="mt-6 flex gap-6 border-b border-border">
          <TabButton
            active={tab === 'watchlist'}
            onClick={() => setTab('watchlist')}
            count={String(watching)}
          >
            Watchlist
          </TabButton>
          <TabButton
            active={tab === 'updates'}
            onClick={() => setTab('updates')}
            count={unread > 0 ? `${unread} new` : ''}
            news
          >
            Updates
          </TabButton>
          <TabButton
            active={tab === 'passed'}
            onClick={() => setTab('passed')}
            count={String(passed)}
          >
            Passed
          </TabButton>
        </div>

        <div className="mt-4">
          <WatchdogBox />
        </div>
        <div className="mt-4">
          {tab === 'watchlist' && (
            <div className="space-y-6">
              <WatchlistTab openId={open ?? null} onOpen={setOpen} />
              <InvitesSection />
            </div>
          )}
          {tab === 'updates' && <UpdatesTab onOpen={setOpen} />}
          {tab === 'passed' && <PassedTab openId={open ?? null} onOpen={setOpen} />}
        </div>
      </div>
    </AssistantGate>
  )
}
